//Proofed
import { useState, useEffect, useRef } from 'react'
import type { Stripe, StripeElements } from '@stripe/stripe-js'
import {
    Box, Container, Typography, TextField, Stack, Stepper, Step, StepLabel,
    Card, CardContent, Chip, CircularProgress, FormControlLabel, Checkbox,
    Alert, LinearProgress, Tooltip,
    Dialog, DialogContent, Grid,
    StepConnector, stepConnectorClasses,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import type { StepIconProps } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CheckIcon from '@mui/icons-material/Check'
import CancelIcon from '@mui/icons-material/Cancel'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import { COLOR_PRIMARY, COLOR_TERTIARY, NAVBAR_BG, DARK_800, BG_LIGHT, COLOR_QUATERNARY, BORDER_BLACK, SECTION_DARK, DARK_600, BORDER_WHITE, CHECKCIRCLE_SIZE } from '../theme'
import { buildCoreUrl, buildCoreApiUrl, pwdCheck, readCookie, getIp, loginpage } from '../common/common'
import { FancyGoldButton } from '../components/FancyGoldButton'
import { FancyLightButton } from '../components/FancyLightButton'
import { HeroHighlightsPanel } from '../components/HeroHighlightsPanel'
import { PricingTierCard } from '../components/PricingTierCard'
import { coreTiers, coreTiersRegister } from '../common/PricingContent'
import { Helmet } from 'react-helmet-async'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import visaImg from '../img/1.png'
import mastercardImg from '../img/2.png'
import amexImg from '../img/22.png'
import stripeLogo from '../img/stripe-wordmark-slate.svg'
import posthog, { isPosthogEnabled, posthogLogger } from '../posthog'

const STEPS = ['Email address', 'Your details', 'Choose your plan', 'Sign up']

function StripePaymentField({ stripeRef, elementsRef, onCompleteChange, onError }: {
    stripeRef: React.MutableRefObject<Stripe | null>
    elementsRef: React.MutableRefObject<StripeElements | null>
    onCompleteChange: (v: boolean) => void
    onError: (msg: string) => void
}) {
    const stripe = useStripe()
    const elements = useElements()
    stripeRef.current = stripe
    elementsRef.current = elements

    const handleChange = (e: import('@stripe/stripe-js').StripePaymentElementChangeEvent) => {
        const brand = (e.value as { card?: { brand?: string } })?.card?.brand
        if (brand === 'discover') {
            onError('Discover cards are not accepted. Please use Visa, Mastercard, or Amex.')
            onCompleteChange(false)
        } else {
            onError('')
            onCompleteChange(e.complete)
        }
    }

    return (
        <PaymentElement
            onChange={handleChange}
            options={{ layout: 'tabs', wallets: { applePay: 'never', googlePay: 'never' } }}
        />
    )
}

// Connector hidden because the gradient LinearProgress bar renders behind the icons instead.
const InvisibleConnector = styled(StepConnector)({
    [`& .${stepConnectorClasses.line}`]: { border: 0, background: 'transparent' },
})

function GoldStepIcon({ active, completed, icon }: StepIconProps) {
    return (
        <Box sx={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            // Solid white fill on inactive so the gradient bar passing behind doesn't bleed through.
            bgcolor: active || completed ? COLOR_PRIMARY : 'white',
            // border: active || completed ? 'none' : '4px solid rgba(0,0,0,0.15)',
            border: active || completed ? 'none' : '4px solid rgba(216, 216, 216)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 400,
            fontSize: '18px',
            color: active || completed ? 'white' : 'rgba(158, 158, 158)',
            position: 'relative',
            zIndex: 1,
        }}>
            {completed ? <CheckIcon sx={{ fontSize: 22, color: 'white' }} /> : icon}
        </Box>
    )
}

// Maps a Core-tier display name to the subtype expected by the registration API.
// Enterprise is not registerable here (its CTA is a phone call).
const tierNameToSubtype: Record<string, 'sme' | 'corp' | null> = {
    Basic: 'sme',
    Professional: 'corp',
    Enterprise: null,
}

type Step4State = 'form' | 'processing' | 'error'

interface Discheck {
    result: boolean
    hassub: boolean
    hadtrial: boolean
    amount: number
    vatamount: number
    firstbilldate: string
    freetermstring: string
    collectday: string
    collectmonth: string
    billline: string
    discount: number
    regprice: number
    setupintentid: string
    publishablekey: string
    promoline: string
    discfootnote: string
}

export default function Register() {
    const [refcode] = useState(() => readCookie('refcode'))
    const [promoMessage, setPromoMessage] = useState('')
    const [step, setStep] = useState(0)
    const [email, setEmail] = useState('')
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [password, setPassword] = useState('')
    const [subtype, setSubtype] = useState<'sme' | 'corp'>('corp')
    const [term, setTerm] = useState<'annual' | 'monthly'>('annual')
    const [discheck, setDischeck] = useState<Discheck | null>(null)
    const [cardComplete, setCardComplete] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [tncChecked, setTncChecked] = useState(false)
    const [subnowChecked, setSubnowChecked] = useState(false)
    const [step4state, setStep4state] = useState<Step4State>('form')
    const [stripePromise, setStripePromise] = useState<ReturnType<typeof loadStripe> | null>(null)
    const [bankUrl, setBankUrl] = useState<string | null>(null)
    const [discountPricing, setDiscountPricing] = useState<{ sme: Discheck | null, corp: Discheck | null }>({ sme: null, corp: null })

    // Refs for values used inside async callbacks / event listeners
    const emailRef = useRef(email)
    const passwordRef = useRef(password)
    const subtypeRef = useRef(subtype)
    const termRef = useRef(term)
    const stripeRef = useRef<Stripe | null>(null)
    const elementsRef = useRef<StripeElements | null>(null)
    const subidRef = useRef('')
    const custidRef = useRef('')
    const setupidRef = useRef('')
    const setupClientSecretRef = useRef('')
    const pmidRef = useRef('')
    const attemptRef = useRef(0)
    const threeDSPollTimeoutRef = useRef<number | null>(null)
    const threeDSCompletionStartedRef = useRef(false)

    useEffect(() => { emailRef.current = email }, [email])
    useEffect(() => { passwordRef.current = password }, [password])
    useEffect(() => { subtypeRef.current = subtype }, [subtype])
    useEffect(() => { termRef.current = term }, [term])

    useEffect(() => {
        if (refcode) {
            fetch(buildCoreApiUrl(`api/refcheck?r=${encodeURIComponent(refcode)}`))
                .then(res => res.json())
                .then(data => { if (data.valid) setPromoMessage(data.message) })
                .catch(() => { })
        }
    }, [])

    const on3DSCompleteRef = useRef<() => void>(() => { })

    const clearThreeDSPoll = () => {
        if (threeDSPollTimeoutRef.current !== null) {
            window.clearTimeout(threeDSPollTimeoutRef.current)
            threeDSPollTimeoutRef.current = null
        }
    }

    const failStep4 = (msg: string) => {
        setError(msg)
        if (attemptRef.current >= 3) setStep4state('error')
        else setStep4state('form')
    }

    const complete3DS = () => {
        if (threeDSCompletionStartedRef.current) return
        threeDSCompletionStartedRef.current = true
        clearThreeDSPoll()
        setBankUrl(null)
        on3DSCompleteRef.current()
    }

    const start3DSStatusPoll = () => {
        clearThreeDSPoll()
        const clientSecret = setupClientSecretRef.current
        if (!clientSecret) return

        const poll = async () => {
            if (threeDSCompletionStartedRef.current) return

            try {
                const stripe = stripeRef.current
                if (!stripe) {
                    threeDSPollTimeoutRef.current = window.setTimeout(poll, 1200)
                    return
                }

                const { setupIntent } = await stripe.retrieveSetupIntent(clientSecret)
                const status = setupIntent?.status

                if (status === 'succeeded') {
                    complete3DS()
                    return
                }

                if (status === 'requires_payment_method' || status === 'canceled') {
                    clearThreeDSPoll()
                    setBankUrl(null)
                    failStep4('Error with payment card. Please check details')
                    return
                }
            } catch {
                // Ignore transient poll failures and retry.
            }

            threeDSPollTimeoutRef.current = window.setTimeout(poll, 1200)
        }

        threeDSPollTimeoutRef.current = window.setTimeout(poll, 1200)
    }

    // Listen for 3DS completion posted from the return-URL page inside the modal iframe.
    // The return-URL page should call: window.parent.postMessage('3DS-authentication-complete', '*')
    useEffect(() => {
        const handler = (event: MessageEvent) => {
            if (event.data === '3DS-authentication-complete') {
                complete3DS()
            }
        }
        window.addEventListener('message', handler)
        return () => {
            window.removeEventListener('message', handler)
            clearThreeDSPoll()
        }
    }, [])

    useEffect(() => {
        if (step === 2 && refcode) {
            const fetchPricing = async () => {
                try {
                    const [smeRes, corpRes] = await Promise.all([
                        fetch(buildCoreApiUrl(`api/discheck?e=${encodeURIComponent(email)}&s=sme&t=${term}&r=${encodeURIComponent(refcode)}`)),
                        fetch(buildCoreApiUrl(`api/discheck?e=${encodeURIComponent(email)}&s=corp&t=${term}&r=${encodeURIComponent(refcode)}`)),
                    ])
                    const smeData: Discheck = await smeRes.json()
                    const corpData: Discheck = await corpRes.json()
                    setDiscountPricing({ sme: smeData, corp: corpData })
                } catch { }
            }
            fetchPricing()
        }
    }, [step, term])

    const apiGet = (path: string) => fetch(buildCoreApiUrl(path))
    const apiPost = (path: string, body: object) => fetch(buildCoreApiUrl(path), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    })

    // ── Step 1 ────────────────────────────────────────────────────────────────
    const handleStep1 = () => {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError('Please enter a valid email address'); return
        }
        setError('')
        if (isPosthogEnabled) {
            posthog.capture('registration_email_submitted')
        }
        // Stripe is initialised at plan-select from the publishable key the server returns
        // (api/discheck) - the client no longer knows or decides test-vs-live.
        setStep(1)
    }

    // ── Step 2 ────────────────────────────────────────────────────────────────
    const handleStep2 = async () => {
        const pc = pwdCheck(password)
        if (firstName.replace(/[^A-Za-z]/g, '').length < 2) { setError('Please enter your first name'); return }
        if (lastName.replace(/[^A-Za-z]/g, '').length < 3) { setError('Please enter your last name'); return }
        if (!Object.values(pc).every(Boolean)) { setError('Please choose a stronger password'); return }
        setLoading(true); setError('')
        try {
            const refcode = readCookie('refcode')
            const ip = await getIp()
            const res = await apiPost('api/regstep2', { e: email, p: password, f: firstName, l: lastName, r: refcode, a: ip })
            const data = await res.json()
            if (data.success) {
                if (isPosthogEnabled) {
                    posthog.capture('registration_details_submitted')
                }
                setStep(2)
            }
            else setError(data.msg ?? 'There appears to be a problem with your account. Please contact sales@contractfinderpro.com')
        } catch { setError('An error occurred. Please try again.') }
        finally { setLoading(false) }
    }

    // ── Step 3 ────────────────────────────────────────────────────────────────
    const handlePlanSelect = async (selectedSubtype: 'sme' | 'corp', selectedTerm: 'annual' | 'monthly') => {
        setSubtype(selectedSubtype); setTerm(selectedTerm)
        subtypeRef.current = selectedSubtype; termRef.current = selectedTerm
        setLoading(true); setError('')
        try {
            const res = await apiGet(`api/discheck?e=${encodeURIComponent(email)}&s=${selectedSubtype}&t=${selectedTerm}${refcode ? `&r=${encodeURIComponent(refcode)}` : ''}`)
            const data: Discheck = await res.json()
            if (data.result) {
                if (isPosthogEnabled) {
                    posthog.capture('registration_plan_selected', {
                        plan: selectedSubtype,
                        billing_term: selectedTerm,
                    })
                }
                posthogLogger.info('registration_plan_selection_succeeded', {
                    plan: selectedSubtype,
                    billing_term: selectedTerm,
                })
                setDischeck(data)
                // Server decides test-vs-live and returns the matching publishable key.
                if (data.publishablekey) setStripePromise(loadStripe(data.publishablekey))
                if (data.hassub) {
                    // Already subscribed  redirect to login after countdown
                    let t = 10
                    const timer = setInterval(() => {
                        if (--t <= 0) { clearInterval(timer); window.location.href = buildCoreUrl(loginpage) }
                    }, 1000)
                } else {
                }
                setStep(3)
            }
        } catch { setError('An error occurred. Please try again.') }
        finally { setLoading(false) }
    }

    // ── Step 4 ────────────────────────────────────────────────────────────────
    const setupSub = async (pmid: string) => {
        try {
            const res = await apiPost('api/setupsub', { pmid, e: email, p: password, s: subtypeRef.current, t: termRef.current, r: refcode || '' })
            const data = await res.json()
            if (data.valid) {
                subidRef.current = data.subid ?? ''
                custidRef.current = data.custid ?? ''
                setupidRef.current = data.setupid ?? ''
                return {
                    subid: data.subid ?? '',
                    custid: data.custid ?? '',
                    setupid: data.setupid ?? '',
                    bankurl: data.bankurl ?? null,
                    clientsecret: data.client_secret ?? '',
                }
            }
            setError('Error setting up subscription. Please try again.')
        } catch { setError('An error occurred. Please try again.') }
        return null
    }

    const handleStep4 = async () => {
        if (discheck?.hadtrial && !subnowChecked) { setError('Please confirm you acknowledge we will take payment today'); return }
        if (!tncChecked) { setError('Please agree to the Terms and Conditions'); return }
        if (!cardComplete) { setError('Please enter your card details'); return }
        const stripe = stripeRef.current
        const elements = elementsRef.current
        if (!stripe || !elements) return
        setError('')
        attemptRef.current += 1
        setStep4state('processing')
        clearThreeDSPoll()
        threeDSCompletionStartedRef.current = false
        setupClientSecretRef.current = ''

        const { error: submitError } = await elements.submit()
        if (submitError) { failStep4(submitError.message ?? 'Please check your card details'); return }

        const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
            elements,
            params: { billing_details: { name: `${firstName} ${lastName}` } },
        })
        if (pmError) { failStep4(pmError.message ?? 'Please check your card details'); return }

        const result = await setupSub(paymentMethod.id)
        if (!result) { if (attemptRef.current >= 3) setStep4state('error'); else setStep4state('form'); return }
        setupClientSecretRef.current = result.clientsecret

        if (result.bankurl) {
            setBankUrl(result.bankurl)
            start3DSStatusPoll()
        } else {
            setupidRef.current = result.setupid
            pmidRef.current = paymentMethod.id
            threeDSCompletionStartedRef.current = true
            on3DSComplete()
        }
    }

    const on3DSComplete = async () => {
        try {
            const ip = await getIp()
            const res = await apiPost('api/confirmsub', { e: emailRef.current, p: passwordRef.current, subid: subidRef.current, custid: custidRef.current, setupid: setupidRef.current })
            const data = await res.json()
            if (data.valid) {
                if (isPosthogEnabled) {
                    posthog.capture('trial_registration_completed', {
                        plan: subtypeRef.current,
                        billing_term: termRef.current,
                    })
                }
                posthogLogger.info('trial_registration_completed', {
                    plan: subtypeRef.current,
                    billing_term: termRef.current,
                })
                // Server omits eventtopush for test accounts, so no client-side staff check is needed.
                if (data.eventtopush) {
                    (window as any).dataLayer?.push({ event: data.eventtopush })
                }
                const tokenRes = await fetch(buildCoreApiUrl('api/token'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: new URLSearchParams({ username: emailRef.current, password: passwordRef.current, grant_type: 'password', ip }).toString(),
                })
                const tokenData = await tokenRes.json()
                const host = window.location.hostname.toLowerCase()
                const sharedDomain = host === 'contractfinderpro.com' || host.endsWith('.contractfinderpro.com')
                const cookieParts = ['Path=/', 'SameSite=Lax', `Max-Age=${60 * 60 * 24 * 30}`]
                if (sharedDomain) cookieParts.push('Domain=.contractfinderpro.com')
                if (window.location.protocol === 'https:') cookieParts.push('Secure')
                if (tokenData.access_token) {
                    localStorage.setItem('access_token', tokenData.access_token)
                    document.cookie = `cfp_access_token=${encodeURIComponent(tokenData.access_token)}; ${cookieParts.join('; ')}`
                }
                if (tokenData.refresh_token) {
                    localStorage.setItem('refresh_token', tokenData.refresh_token)
                    document.cookie = `cfp_refresh_token=${encodeURIComponent(tokenData.refresh_token)}; ${cookieParts.join('; ')}`
                }
                window.location.href = '/register-complete'
            } else {
                setError('Error with payment card. Please check details')
                if (attemptRef.current >= 3) setStep4state('error')
                else setStep4state('form')
            }
        } catch { setError('An error occurred. Please try again.'); setStep4state('form') }
    }
    on3DSCompleteRef.current = on3DSComplete

    const pc = pwdCheck(password)

    return (
        <Box sx={{
            //minHeight: '100vh',
            background: BG_LIGHT
        }}>
            <Helmet>
                <title>Start Your Free Trial | Contract Finder Pro</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            {/* ── Hero ── */}
            <Box sx={{ background: `linear-gradient(135deg, ${NAVBAR_BG} 0%, ${DARK_800} 100%)`, pt: { xs: 8, md: 6 }, pb: 1, textAlign: 'center' }}>
                <Container maxWidth="md">
                    <Typography variant="h1" sx={{ color: 'white', fontSize: { xs: '28px', md: '40px' }, mb: 1 }}>
                        Start your 14 day free trial
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '17px' }}>
                        Discover contract opportunities perfect for your business
                        {/* Get daily personalised email alerts of public sector opportunities perfect for your business */}
                        {/* Full access. Cancel any time. */}
                    </Typography>
                </Container>
            </Box>

            <Box sx={{
                background: `linear-gradient(135deg, ${NAVBAR_BG} 0%, ${DARK_600} 100%)`,
                ...SECTION_DARK,
                py: 1, textAlign: 'center', pb: 5
            }}>
                <Container maxWidth="md" sx={{
                    display: 'flex',
                    // border: BORDER_WHITE,
                    justifyContent: 'space-around',
                    alignItems: 'center'
                }}>
                    <Typography variant="h1" sx={{ color: 'white', fontSize: { xs: '36px', md: '52px' } }}>
                        CFP <Box component="span" sx={{ color: COLOR_PRIMARY }}>Alerts</Box>
                    </Typography>
                    <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 1.5,
                        backgroundColor: `${COLOR_PRIMARY}12`,
                        border: `1px solid ${COLOR_PRIMARY}70`,
                        borderRadius: 2, px: 2, py: 1
                    }}>
                        <Box sx={{
                            width: 32, height: 32, borderRadius: 1,
                            backgroundColor: `${COLOR_PRIMARY}20`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <NotificationsActiveIcon sx={{ color: COLOR_PRIMARY, fontSize: 22 }} />
                        </Box>
                        <Typography variant="caption" sx={{ color: COLOR_PRIMARY, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                            Tender Alerting
                        </Typography>
                    </Box>

                    {/* <Typography variant="h4" sx={{ fontSize: { xs: '28px', md: '40px' }, mb: 1 }}>
                        CFP Core
                    </Typography> */}
                    {/* <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '17px' }}>
                        Full access. Cancel any time.
                    </Typography> */}
                </Container>
            </Box>
            {/* ── Combined progress bar + stepper ──
                 Full-width gradient bar runs edge-to-edge of the window behind the stepper.
                 The stepper itself is capped at STEPPER_WIDTH and centred, so the icons
                 cluster in the middle of the window while the bar extends out to the edges.
                 The whole block is lifted up so its vertical centre straddles the seam
                 between the dark hero section above and the white form area below. */}
            {(() => {
                const STEPPER_WIDTH = 650 // px cap for the icon band
                const halfWidth = STEPPER_WIDTH / 2
                const perStepPx = STEPPER_WIDTH / STEPS.length
                const perStepPct = 100 / STEPS.length
                // Horizontal position of the current step icon centre from the window's left edge:
                //   container_left_padding + (step + 0.5) * step_width
                // where container_left_padding = max(0, (100vw - STEPPER_WIDTH) / 2) and the step width
                // shrinks on narrow screens via min(perStepPx, perStepPct%).
                const fillWidth = `calc(max(0px, 50% - ${halfWidth}px) + min(${(step + 0.5) * perStepPx}px, ${(step + 0.5) * perStepPct}%))`
                return (
                    <Box sx={{
                        position: 'relative',
                        mt: '-16px', // ~half the stepper (icon + label) height so the block centres on the dark/light seam
                        zIndex: 2,
                    }}>
                        {/* Full-window-width track with a gradient fill whose right edge lands on the active step icon */}
                        <Box sx={{
                            position: 'absolute',
                            top: 15, // 19px (centre of 38px icon) minus 4px (half of 8px bar)
                            left: 0,
                            right: 0,
                            height: 8,
                            backgroundColor: 'rgba(0,0,0,0.12)',
                            zIndex: 0,
                            overflow: 'hidden',
                        }}>
                            <Box sx={{
                                width: fillWidth,
                                height: '100%',
                                backgroundImage: 'linear-gradient(90deg, #c0392b 0%, #f39c12 50%, #f1c40f 100%)',
                                transition: 'width 0.5s ease',
                            }} />
                        </Box>
                        <Stepper
                            activeStep={step}
                            alternativeLabel
                            connector={<InvisibleConnector />}
                            sx={{
                                position: 'relative', zIndex: 1,
                                maxWidth: `${STEPPER_WIDTH}px`,
                                width: '100%',
                                mx: 'auto',
                            }}
                        >
                            {STEPS.map(label => (
                                <Step key={label}>
                                    <StepLabel
                                        StepIconComponent={GoldStepIcon}
                                        sx={{ '& .MuiStepLabel-label': { fontSize: '14px' } }}
                                    >
                                        {label}
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                )
            })()}

            {/* ── Form card ── */}
            <Container maxWidth={step === 3 ? 'lg' : step === 2 ? 'lg' : 'sm'} sx={{
                minHeight: '500px',
                py: { xs: 4, md: 1 },
                // mb: 5
                mb: 2
            }}>
                <Card elevation={2} sx={{ borderRadius: 3 }}>
                    <CardContent sx={{ p: { xs: 3, md: 1 } }}>

                        {/* Step 0  Email */}
                        {step === 0 && (
                            <Box>
                                {promoMessage && (
                                    <Box sx={{
                                        mb: 3, p: 2, borderRadius: 2,
                                        bgcolor: `${COLOR_PRIMARY}15`,
                                        border: `2px solid ${COLOR_PRIMARY}`,
                                        textAlign: 'center',
                                    }}>
                                        <Typography sx={{ color: NAVBAR_BG, fontWeight: 700, fontSize: '16px' }}>
                                            {promoMessage}
                                        </Typography>
                                    </Box>
                                )}
                                <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Sign up with your email</Typography>
                                {/* <Typography color="text.secondary" sx={{ mb: 3, fontSize: '14px' }}>Start your 14 day free trial</Typography> */}
                                <TextField
                                    fullWidth type="email" label="Email Address"
                                    value={email} onChange={e => setEmail(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleStep1()}
                                    sx={{ mb: 2 }}
                                />
                                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                                <Box sx={{ mb: 2 }}>
                                    <FancyGoldButton fullWidth onClick={handleStep1} endIcon={<ArrowForwardIcon />}>
                                        Get Started
                                    </FancyGoldButton>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                                    By clicking "Get Started" you agree to our{' '}
                                    <a href="/privacy" style={{ color: COLOR_PRIMARY }}>Privacy Policy</a> and{' '}
                                    <a href="/terms" style={{ color: COLOR_PRIMARY }}>User Licence Terms</a>
                                </Typography>

                                {/* What you get with your free trial */}
                                <Box sx={{ pt: 2 }}>
                                    <HeroHighlightsPanel
                                        variant="light"
                                        size='sm'
                                        title="What's included in your free trial"
                                        highlights={[
                                            'Personalised daily email alert of new tenders from 200+ portals, tailored to your business',
                                            'Instant same day alerts when urgent tenders are published',
                                            'Competitor and buyer insight tools to beat rivals and understand what buyers want',
                                            'Build a winning strategy using 2 million historic tenders and awards to spot market trends',
                                            'Cancel anytime',
                                        ]}
                                    />
                                </Box>
                            </Box>
                        )}

                        {/* Step 1  Details */}
                        {step === 1 && (
                            <Box>
                                {loading ? (
                                    <Box sx={{ textAlign: 'center', py: 6 }}>
                                        <CircularProgress size={48} sx={{ color: COLOR_PRIMARY, mb: 2 }} />
                                        <Typography fontWeight={600} sx={{ fontSize: '16px' }}>Checking details...</Typography>
                                        <Typography color="text.secondary" sx={{ fontSize: '14px', mt: 0.5 }}>Just a moment</Typography>
                                    </Box>
                                ) : <>
                                    <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>Your details</Typography>
                                    <Stack spacing={2} sx={{ mb: 2 }}>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                            <TextField autoFocus fullWidth label="First Name" value={firstName} onChange={e => setFirstName(e.target.value)} />
                                            <TextField fullWidth label="Last Name" value={lastName} onChange={e => setLastName(e.target.value)} />
                                        </Stack>
                                        <TextField
                                            fullWidth type="password" label="Password"
                                            value={password}
                                            onChange={e => setPassword(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && handleStep2()}
                                        />
                                    </Stack>
                                    {/* Password checker */}
                                    <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 2, mb: 3 }}>
                                        <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                                            Your password must contain
                                        </Typography>
                                        <Box sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                            columnGap: 2,
                                            rowGap: 0.5,
                                        }}>
                                            {([
                                                { ok: pc.length, label: 'At least 8 characters' },
                                                { ok: pc.number, label: 'At least 1 number' },
                                                { ok: pc.lower, label: '1 lowercase letter' },
                                                { ok: pc.upper, label: '1 uppercase letter' },
                                            ] as const).map(({ ok, label }) => (
                                                <Stack key={label} direction="row" alignItems="center" spacing={0.75}>
                                                    {ok
                                                        ? <CheckCircleIcon sx={{ fontSize: CHECKCIRCLE_SIZE, color: 'success.main' }} />
                                                        : <CancelIcon sx={{ fontSize: CHECKCIRCLE_SIZE, color: 'error.light' }} />}
                                                    <Typography variant="caption">{label}</Typography>
                                                </Stack>
                                            ))}
                                        </Box>
                                    </Box>
                                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                                    <Stack direction="row" spacing={2}>
                                        <FancyLightButton onClick={() => { setError(''); setStep(0) }} endIcon={<ArrowBackIcon />}>Back</FancyLightButton>
                                        <FancyGoldButton fullWidth onClick={handleStep2} disabled={loading}
                                            endIcon={loading ? undefined : <ArrowForwardIcon />}>
                                            {loading
                                                ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Checking details...</>
                                                : 'Next'}
                                        </FancyGoldButton>
                                    </Stack>
                                </>}
                            </Box>
                        )}

                        {/* Step 2  Choose plan */}
                        {step === 2 && (
                            <Box>
                                <Typography variant="h5" fontWeight={700} sx={{ mb: 1, textAlign: 'center' }}>Choose your plan</Typography>
                                <Typography color="text.secondary" sx={{ mb: 3, fontSize: '14px', textAlign: 'center' }}>All plans include a 14-day free trial. No charge until your trial ends.</Typography>

                                {/* Yearly / Monthly toggle — mirrors the Pricing page */}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '120px 52px 120px', sm: '140px 52px 140px' },
                                        gridTemplateRows: 'auto auto',
                                        columnGap: 0,
                                        rowGap: '2px',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        mb: 4,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        onClick={() => setTerm('annual')}
                                        sx={{
                                            gridColumn: 1, gridRow: 1, justifySelf: 'center',
                                            cursor: 'pointer', fontWeight: 700, color: 'text.primary',
                                            opacity: term === 'annual' ? 1 : 0.25,
                                            fontSize: '20px', lineHeight: 1.1, transition: 'opacity 0.2s',
                                        }}
                                    >
                                        Yearly
                                    </Typography>
                                    <Box
                                        onClick={() => setTerm(t => t === 'annual' ? 'monthly' : 'annual')}
                                        sx={{
                                            gridColumn: 2, gridRow: 1, justifySelf: 'center',
                                            width: 52, height: 28, borderRadius: 14,
                                            backgroundColor: COLOR_PRIMARY,
                                            position: 'relative', cursor: 'pointer',
                                            transition: 'background-color 0.2s', flexShrink: 0,
                                        }}
                                    >
                                        <Box sx={{
                                            position: 'absolute',
                                            top: 3, left: term === 'monthly' ? 27 : 3,
                                            width: 22, height: 22, borderRadius: '50%',
                                            backgroundColor: 'white',
                                            transition: 'left 0.2s',
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                                        }} />
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        onClick={() => setTerm('monthly')}
                                        sx={{
                                            gridColumn: 3, gridRow: 1, justifySelf: 'center',
                                            cursor: 'pointer', fontWeight: 700, color: 'text.primary',
                                            opacity: term === 'monthly' ? 1 : 0.25,
                                            fontSize: '20px', lineHeight: 1.1, transition: 'opacity 0.2s',
                                        }}
                                    >
                                        Monthly
                                    </Typography>
                                    <Chip
                                        label="2 months free"
                                        size="small"
                                        onClick={() => setTerm('annual')}
                                        sx={{
                                            gridColumn: 1, gridRow: 2, justifySelf: 'center',
                                            height: '20px', cursor: 'pointer',
                                            backgroundColor: term === 'annual' ? `${COLOR_TERTIARY}18` : 'rgba(0,0,0,0.04)',
                                            color: term === 'annual' ? COLOR_TERTIARY : 'text.disabled',
                                            fontWeight: 700,
                                            border: term === 'annual' ? `1px solid ${COLOR_TERTIARY}40` : '1px solid rgba(0,0,0,0.12)',
                                            opacity: term === 'annual' ? 1 : 0.65,
                                            transition: 'all 0.2s',
                                            '& .MuiChip-label': {
                                                fontSize: '12px', lineHeight: '20px', px: 1.1,
                                            },
                                        }}
                                    />
                                </Box>

                                {/* Plan cards — shared PricingTierCard component. Enterprise CTA is a phone call. */}
                                <Grid container spacing={3} alignItems="stretch" justifyContent="center" sx={{ mb: 3 }}>
                                    {coreTiersRegister.map(tier => {
                                        const subtypeForTier = tierNameToSubtype[tier.name] ?? null
                                        return (
                                            <Grid item xs={12} md={4} key={tier.name}>
                                                <PricingTierCard
                                                    tier={tier}
                                                    billingCycle={term}
                                                    discountInfo={subtypeForTier ? discountPricing[subtypeForTier] : null}
                                                    {...(subtypeForTier
                                                        ? {
                                                            onJoinClick: () => !loading && handlePlanSelect(subtypeForTier, term),
                                                        }
                                                        : {})}
                                                />
                                            </Grid>
                                        )
                                    })}
                                </Grid>

                                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                                {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
                                <FancyLightButton onClick={() => { setError(''); setStep(1) }} endIcon={<ArrowBackIcon />}>Back</FancyLightButton>
                            </Box>
                        )}

                        {/* Step 3  Payment */}
                        {step === 3 && (
                            <Box>
                                {step4state === 'processing' && (
                                    <Box sx={{ textAlign: 'center', py: 6 }}>
                                        <CircularProgress size={48} sx={{ color: COLOR_PRIMARY, mb: 2 }} />
                                        <Typography fontWeight={600} sx={{ fontSize: '16px' }}>Processing your payment…</Typography>
                                        <Typography color="text.secondary" sx={{ fontSize: '14px', mt: 0.5 }}>Please do not refresh this page</Typography>
                                    </Box>
                                )}
                                {discheck?.hassub ? (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                                        <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>You already have a subscription!</Typography>
                                        <Typography color="text.secondary">Redirecting you to log in shortly...</Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: step4state === 'processing' ? 'none' : undefined }}>
                                        {discheck?.hadtrial && (
                                            <Alert severity="warning" sx={{ mb: 3 }}>
                                                Welcome back! Your free trial has expired. By proceeding, your subscription begins today.
                                            </Alert>
                                        )}
                                        <Box sx={{ flex: 1, pr: { md: 4 } }}>
                                            <Typography variant="h6" fontWeight={700} textAlign="center" sx={{ mb: 0.75 }}>
                                                Please provide your credit card details
                                            </Typography>
                                            <Typography textAlign="center" color="text.secondary" sx={{ fontSize: '14px', mb: 2.5 }}>
                                                {discheck?.hadtrial
                                                    ? 'Your card will be charged today'
                                                    : 'There is no charge during your 14 day free trial'}
                                            </Typography>
                                        </Box>
                                        {discheck && discheck.discount > 0 && discheck.promoline && (
                                            <Box sx={{
                                                mb: 2, p: 2, borderRadius: 2,
                                                background: `linear-gradient(135deg, ${NAVBAR_BG} 0%, ${DARK_600} 100%)`,
                                                border: `2px solid ${COLOR_PRIMARY}`,
                                                textAlign: 'center',
                                            }}>
                                                <Typography sx={{ color: COLOR_PRIMARY, fontWeight: 700, fontSize: '16px' }}>
                                                    {discheck.promoline}
                                                </Typography>
                                            </Box>
                                        )}
                                        {/* Two-column layout */}
                                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={0} >
                                            {/* ── Left: Card details ── */}
                                            <Box sx={{ flex: 1, pr: { md: 4 }, borderRight: { md: '2px solid  rgba(0,0,0,0.12)' }, pb: { xs: 3, md: 0 } }}>


                                                {/* Stripe Payment Element */}
                                                {stripePromise && (

                                                    <Box sx={{ border: '1px solid rgba(0,0,0,0.23)', borderRadius: 1, mb: 2, mt: 0, overflow: 'hidden' }}>
                                                        <Box sx={{ borderBottom: '1px solid rgba(0,0,0,0.1)', bgcolor: 'rgba(0,0,0,0.02)', px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                                <Box component="a" href="https://www.stripe.com" target="_blank" rel="noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0, textDecoration: 'none' }}>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>Powered by</Typography>
                                                                    <Box component="img" src={stripeLogo} alt="Stripe" sx={{ height: 28 }} />
                                                                </Box>
                                                                <Tooltip
                                                                    title="Contract Finder Pro uses Stripe for payment processing. Stripe has been audited by a PCI-certified auditor and is certified to PCI Service Provider Level 1  the most stringent level of protection in the payments industry."
                                                                    arrow
                                                                    placement="bottom-start"
                                                                >
                                                                    <LockOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled', cursor: 'pointer' }} />
                                                                </Tooltip>
                                                            </Stack>
                                                            {/* <Stack direction="row" spacing={0.75} alignItems="center">
                                                                <Box component="img" src={visaImg} alt="Visa" sx={{ height: 24, borderRadius: '3px' }} />
                                                                <Box component="img" src={mastercardImg} alt="Mastercard" sx={{ height: 24, borderRadius: '3px' }} />
                                                                <Box component="img" src={amexImg} alt="American Express" sx={{ height: 24, borderRadius: '3px' }} />
                                                            </Stack> */}
                                                        </Box>
                                                        <Box sx={{ px: 1.5, pb: 1, pt: 0 }}>
                                                            <Elements stripe={stripePromise} options={{
                                                                mode: 'setup',
                                                                currency: 'gbp',
                                                                paymentMethodCreation: 'manual',
                                                                appearance: {
                                                                    theme: 'stripe',
                                                                    variables: {
                                                                        colorPrimary: DARK_800,
                                                                        fontFamily: '"Inter", Helvetica, sans-serif',
                                                                        borderRadius: '4px',
                                                                    },
                                                                },
                                                            }}>
                                                                <StripePaymentField
                                                                    stripeRef={stripeRef}
                                                                    elementsRef={elementsRef}
                                                                    onCompleteChange={setCardComplete}
                                                                    onError={(msg: string) => { setError(msg); if (attemptRef.current >= 3) setStep4state('error'); else setStep4state('form') }}
                                                                />
                                                            </Elements>
                                                        </Box>

                                                    </Box>
                                                )}
                                            </Box>

                                            {/* ── Right: Package summary ── */}
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                flex: 1, pl: { md: 4 }, pt: { xs: 3, md: 0 }
                                            }}>
                                                <Box sx={{
                                                    border: '1px solid rgba(0,0,0,0.23)',
                                                    borderRadius: 1,
                                                    overflow: 'hidden',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                                }}>
                                                    {/* Card header */}
                                                    <Box sx={{
                                                        bgcolor: '#f5f5f5',
                                                        borderBottom: '1px solid rgba(0,0,0,0.1)',
                                                        px: 2.5, py: 2,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                    }}>
                                                        <Box>
                                                            <Typography variant="caption" sx={{ color: 'text.disabled', letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '10px' }}>
                                                                Your package
                                                            </Typography>
                                                            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, mt: 0.25, fontSize: '16px' }}>
                                                                {subtype === 'sme' ? 'Contract Finder Core Basic' : 'Contract Finder Core Professional'}
                                                            </Typography>
                                                        </Box>
                                                        <Chip
                                                            label={term === 'annual' ? 'Annual' : 'Monthly'}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: COLOR_PRIMARY,
                                                                color: '#fff',
                                                                fontWeight: 800,
                                                                fontSize: '13px',
                                                                height: 24,
                                                                p: 0.5,
                                                                letterSpacing: '0.04em',
                                                            }}
                                                        />
                                                    </Box>

                                                    {discheck && (
                                                        <>
                                                            {/* Detail rows */}
                                                            <Box sx={{ px: 2.5, py: 2, bgcolor: '#fff' }}>
                                                                {[
                                                                    { label: 'Name', value: `${firstName} ${lastName}` },
                                                                    { label: 'Email', value: email, breakWord: true },
                                                                    {
                                                                        label: 'Price', value: `£${Number(discheck.amount).toFixed(2)} / ${term === 'annual' ? 'year' : 'month'} + VAT`,
                                                                        ...(discheck.discount > 0 ? { originalPrice: `£${Number(discheck.amount + discheck.discount).toFixed(2)}` } : {}),
                                                                    },
                                                                    ...(discheck.discount > 0 ? [{ label: 'Discount', value: discheck.billline, sup: '3' }] : []),
                                                                    {
                                                                        label: discheck.hadtrial ? 'Starts' : 'Free trial',
                                                                        value: discheck.hadtrial ? 'Today' : discheck.freetermstring,
                                                                        sup: '1',
                                                                    },
                                                                ].map((row, i) => (
                                                                    <Stack key={i} direction="row" justifyContent="space-between" alignItems="flex-start"
                                                                        sx={{ py: 1, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                                                                        <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '10px', pt: 0.2, whiteSpace: 'nowrap', mr: 1 }}>
                                                                            {row.label}
                                                                        </Typography>
                                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                                            {(row as { originalPrice?: string }).originalPrice && (
                                                                                <Typography variant="body2" fontWeight={500} sx={{
                                                                                    color: 'text.disabled',
                                                                                    position: 'relative',
                                                                                    '&::after': {
                                                                                        content: '""',
                                                                                        position: 'absolute',
                                                                                        left: -2,
                                                                                        right: -2,
                                                                                        top: '45%',
                                                                                        height: '2px',
                                                                                        bgcolor: '#c0392b',
                                                                                        transform: 'rotate(-12deg)',
                                                                                    },
                                                                                }}>
                                                                                    {(row as { originalPrice?: string }).originalPrice}
                                                                                </Typography>
                                                                            )}
                                                                            <Typography variant="body2" fontWeight={500} textAlign="right"
                                                                                sx={{ wordBreak: (row as { breakWord?: boolean }).breakWord ? 'break-all' : undefined }}>
                                                                                {row.value}{row.sup && <sup>{row.sup}</sup>}
                                                                            </Typography>
                                                                        </Stack>
                                                                    </Stack>
                                                                ))}
                                                            </Box>

                                                            {/* Total box */}
                                                            <Box sx={{
                                                                bgcolor: `${COLOR_PRIMARY}12`,
                                                                borderTop: `1px solid ${COLOR_PRIMARY}`,
                                                                px: 2.5, py: 2,
                                                            }}>
                                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                    <Box>
                                                                        <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '10px' }}>
                                                                            {discheck.hadtrial ? 'Total to pay today' : `Due on ${discheck.firstbilldate}`}
                                                                        </Typography>
                                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                                            {discheck.discount > 0 && (
                                                                                <Typography fontWeight={800} fontSize="22px" sx={{
                                                                                    color: 'text.disabled', lineHeight: 1.1,
                                                                                    position: 'relative',
                                                                                    '&::after': {
                                                                                        content: '""',
                                                                                        position: 'absolute',
                                                                                        left: -2, right: -2,
                                                                                        top: '45%',
                                                                                        height: '2px',
                                                                                        bgcolor: '#c0392b',
                                                                                        transform: 'rotate(-12deg)',
                                                                                    },
                                                                                }}>
                                                                                    £{Number(discheck.regprice * 1.2).toFixed(2)}
                                                                                </Typography>
                                                                            )}
                                                                            <Typography fontWeight={800} fontSize="22px" sx={{ color: COLOR_PRIMARY, lineHeight: 1.1 }}>
                                                                                £{Number(discheck.vatamount).toFixed(2)}
                                                                            </Typography>
                                                                        </Stack>
                                                                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '10px' }}>
                                                                            inc. VAT
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box sx={{ textAlign: 'right' }}>
                                                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.5, maxWidth: 120 }}>
                                                                            {discheck.hadtrial
                                                                                ? 'Subscription auto-renews after today'
                                                                                : 'Auto-renews after 14-day free trial'}
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                                                                            Cancel any time<sup>2</sup>
                                                                        </Typography>
                                                                    </Box>
                                                                </Stack>
                                                            </Box>
                                                        </>
                                                    )}
                                                </Box>

                                                {/* Change plan link */}
                                                <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                                                    <Typography
                                                        component="span"
                                                        variant="caption"
                                                        onClick={() => { setStep(2); setError('') }}
                                                        sx={{ color: COLOR_PRIMARY, cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
                                                    >
                                                        Change plan
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Stack>

                                        {/* Footnotes */}
                                        {discheck && (
                                            <Box sx={{ mt: 1, pt: 1 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                                                    <sup>1</sup> First Payment will be taken on {discheck.firstbilldate} and re-occur {term === 'annual' ? 'annually' : 'monthly'} on or around the {discheck.collectday} of the month.
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    <sup>2</sup>{' '}
                                                    {discheck.hadtrial
                                                        ? <>Please cancel online or email <a href="mailto:sales@contractfinderpro.com" style={{ color: COLOR_PRIMARY }}>sales@contractfinderpro.com</a> to cancel your subscription</>
                                                        : <>If you cancel before the first bill date your payment method will not be charged. Please cancel online or email <a href="mailto:sales@contractfinderpro.com" style={{ color: COLOR_PRIMARY }}>sales@contractfinderpro.com</a> to cancel your subscription</>
                                                    }
                                                </Typography>
                                                {discheck.discount > 0 && discheck.discfootnote && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                                        <sup>3</sup> {discheck.discfootnote}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        )}

                    </CardContent>
                </Card>

                {/* Action row for step 3 */}
                {
                    step === 3 && discheck && !discheck.hassub && step4state !== 'processing' && (
                        <Box sx={{ mt: 2 }}>
                            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                            {step4state === 'error' ? (
                                <Alert severity="error">
                                    We had trouble setting up your registration. Please email{' '}
                                    <a href="mailto:sales@contractfinderpro.com" style={{ color: 'inherit' }}>
                                        sales@contractfinderpro.com
                                    </a>{' '}
                                    and we will be happy to sort it out.
                                </Alert>
                            ) : (
                                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={2}>
                                    <FancyLightButton onClick={() => { setStep(2); setError('') }} endIcon={<ArrowBackIcon />}>Back</FancyLightButton>
                                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 0.5 }}>
                                        {discheck?.hadtrial && (
                                            <FormControlLabel
                                                control={<Checkbox checked={subnowChecked} onChange={e => setSubnowChecked(e.target.checked)} />}
                                                label={<Typography variant="body2">I acknowledge that payment will be taken today</Typography>}
                                            />
                                        )}
                                        <FormControlLabel
                                            control={<Checkbox checked={tncChecked} onChange={e => setTncChecked(e.target.checked)} />}
                                            label={
                                                <Typography variant="body2">
                                                    I agree to the{' '}
                                                    <a href="/terms" target="_blank" rel="noreferrer" style={{ color: COLOR_PRIMARY }}>Terms and Conditions</a>
                                                </Typography>
                                            }
                                        />
                                    </Box>
                                    <FancyGoldButton onClick={handleStep4} width={150} disabled={!tncChecked || (!!discheck?.hadtrial && !subnowChecked)}
                                    >Register</FancyGoldButton>
                                </Stack>
                            )}
                        </Box>
                    )
                }
            </Container >

            {/* ── 3DS bank modal ── */}
            <Dialog open={!!bankUrl} maxWidth={false} PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
                <DialogContent sx={{ p: 0 }}>
                    {bankUrl && (
                        <Box component="iframe" src={bankUrl} sx={{ display: 'block', border: 'none', width: 500, height: 600 }} />
                    )}
                </DialogContent>
            </Dialog>
        </Box >
    )
}
